import { useCallback, useRef, useState } from 'react';
import { TextInput } from 'react-native';
import { INPUT_STATE } from '../types';

interface UseInputProps {
  value?: string;
  defaultValue?: string;
  errorText?: string;
  disabled?: boolean;
  onValueChange?: (value?: string) => void;
  onBlur?: () => void;
}

export const useInput = (props: UseInputProps) => {
  const { onValueChange, onBlur, errorText, disabled } = props;

  const [value, setValue] = useState<string>(props.defaultValue || props.value || '');
  const [inputState, setInputState] = useState<INPUT_STATE>(
    disabled ? INPUT_STATE.DISABLED : !!errorText ? INPUT_STATE.ERROR : INPUT_STATE.DEFAULT
  );

  const inputRef = useRef<TextInput>(null);

  /**
   * callback function that updates the input value, it takes an optional parameter
   *
   * @param value
   */
  const onChange = useCallback((value?: string) => {
    let input = value || '';
    setValue(input);
    onValueChange && onValueChange(input);
  }, []);

  /**
   * callback function that calls onValueChange if provided whenever the value changes
   */
  useCallback(() => {
    onValueChange && onValueChange(value);
  }, [value]);

  /**
   * callback function that clears the input value.
   */
  function onClear() {
    setValue('');
  }

  /**
   * callback function that focuses the input
   */
  function focus() {
    if (disabled) {
      return;
    }
    inputRef.current?.focus();
    setInputState(!!errorText ? INPUT_STATE.ERROR : INPUT_STATE.FOCUSED);
  }

  /**
   * callback function that blurs the input
   * @param callback
   */
  function blur() {
    inputRef.current?.blur();
    onBlur && onBlur();
    setInputState(!!errorText ? INPUT_STATE.ERROR : INPUT_STATE.DEFAULT);
  }

  return {
    value,
    inputRef,
    inputState,
    onChange,
    onClear,
    onFocus: focus,
    onBlur: blur
  };
};
